import { useCallback, useEffect, useMemo, useRef } from 'react';
import tw from 'twin.macro';

import { useIntegrationsContext } from '@insights_module/context/IntegrationsContext';
import { CloseIcon } from '@icons/CloseIcon';
import { Loader } from '@components/Loader';
import { Modal } from '@components/Modal';
import { useInfiniteTrackingPixels } from '@insights_module/hooks/integrations/useInfiniteTrackingPixels';

const TABLE_ROW_HEIGHT = '48';
const FETCH_MORE_OFFSET = 4 * Number(TABLE_ROW_HEIGHT);
const gridTemplateColumns = '3fr 2fr 2fr 2fr';

const Wrapper = tw.div`w-full max-w-[1106px] min-h-[350px] lg:w-[1106px] h-full bg-page-background shadow-modal z-50 mx-auto`;
const Title = tw.div`flex items-center justify-between py-[8px] px-[16px] bg-neutral-background border-t border-t-gray-1 text-[13px] text-text-and-icon font-bold`;
const CloseIconWrapper = tw.div`flex justify-center items-center cursor-pointer rounded h-[20px] w-[20px] hover:opacity-75`;
const TableWrapper = tw.div`h-[calc(100% - 36px)]`;
const TableInnerWrapper = tw.div`relative w-full h-full`;
const TableHeader = tw.div`h-[34px] grid items-center top-[-1px] sticky z-20 text-text-and-icon-muted-2 text-[11px] font-semibold bg-neutral-background border-b border-t border-gray-1 px-[16px]`;
const TableHeaderCell = tw.div`flex items-center justify-start text-[12px] text-text-and-icon-muted-2 font-semibold`;
const TableBodyWrapper = tw.div`absolute top-[34px] right-0 bottom-0 left-0`;
const TableBody = tw.div`bg-neutral-background w-full h-full overflow-y-auto`;
const TableBodyRow = tw.div`grid h-[${TABLE_ROW_HEIGHT}px] items-center px-[16px] border-b border-gray-1 gap-[0 30px] xl:gap-[0 60px]`;
const TableBodyCell = tw.div`max-h-[${TABLE_ROW_HEIGHT}px] h-[${TABLE_ROW_HEIGHT}px] flex items-center justify-start text-[12px] text-text-and-icon-muted font-medium truncate`;
const TableEnd = tw.div`w-full h-[1px]`;
const LoadingOverlay = tw.div`absolute top-[34px] left-0 right-0 bottom-0 z-20 bg-neutral-background flex items-center justify-center`;
const NoDataFallbackWrapper = tw.div`flex flex-col justify-center items-center gap-[12px]`;

const LoadingRow = ({ isLoading }: { isLoading: boolean | undefined }): JSX.Element => (
  <TableBodyRow
    css={[
      tw`transition-all duration-200`,
      isLoading ? tw`h-10 flex items-center justify-center` : tw`h-0 hidden`,
    ]}
    data-testid='load-more-row'>
    <Loader size='sm' theme='dark' />
  </TableBodyRow>
);

export const TrackingPixelTableModal = () => {
  const endOfTableRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);

  const { showTrackingPixelTableModal, setShowTrackingPixelTableModal } = useIntegrationsContext();
  const {
    trackingPixels,
    isLoading,
    isLoadingMore,
    totalTrackingPixels,
    isInitialLoading,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteTrackingPixels();

  const triggerFetchMore = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (!isLoading && entry.isIntersecting) {
          !isLoadingMore && hasNextPage && fetchNextPage();
        }
      });
    },
    [isLoading, isLoadingMore, fetchNextPage]
  );

  useEffect(() => {
    if (!endOfTableRef.current || !tableRef.current) return;

    const observer = new IntersectionObserver(triggerFetchMore, {
      root: tableRef.current,
      rootMargin: FETCH_MORE_OFFSET + 'px',
      threshold: 0.1,
    });
    observer.observe(endOfTableRef.current);

    return () => {
      if (endOfTableRef.current) {
        observer.unobserve(endOfTableRef.current);
      }
    };
  }, [triggerFetchMore]);

  const noData = useMemo(
    () => !totalTrackingPixels && !isLoading && !isLoadingMore && !isInitialLoading,
    [totalTrackingPixels, isLoading, isLoadingMore, isInitialLoading]
  );

  const TableBodyElement = useMemo(() => {
    if (noData) {
      return (
        <NoDataFallbackWrapper>
          <div tw='flex flex-col justify-center items-center'>
            <Title tw='bg-page-background border-none text-text-and-icon-muted-2'>
              No pixels yet
            </Title>
          </div>
        </NoDataFallbackWrapper>
      );
    }

    return (
      <TableBody>
        {trackingPixels.map((pixel, index) => {
          return (
            <TableBodyRow css={{ gridTemplateColumns }} key={index}>
              <TableBodyCell>
                <div tw='truncate text-text-and-icon font-semibold'>
                  {pixel?.tracking_pixel_url}
                </div>
              </TableBodyCell>

              <TableBodyCell>{pixel?.campaign_id || '-'}</TableBodyCell>

              <TableBodyCell>{pixel?.campaign_child_id || '-'}</TableBodyCell>

              <TableBodyCell>
                {pixel?.created_at ? new Date(pixel?.created_at).toLocaleDateString() : '-'}
              </TableBodyCell>
            </TableBodyRow>
          );
        })}
        <LoadingRow isLoading={isLoadingMore} />
        <TableEnd ref={endOfTableRef} />
      </TableBody>
    );
  }, [noData, trackingPixels, isLoadingMore, gridTemplateColumns]);

  if (!showTrackingPixelTableModal) return null;

  return (
    <Modal onClose={() => setShowTrackingPixelTableModal(false)}>
      <Wrapper>
        <Title>
          Pixels List
          <CloseIconWrapper onClick={() => setShowTrackingPixelTableModal(false)}>
            <CloseIcon tw='fill-text-and-icon h-[10px] w-[10px]' />
          </CloseIconWrapper>
        </Title>

        <TableWrapper>
          {isInitialLoading && (
            <LoadingOverlay>
              <Loader theme='dark' size='lg' />
            </LoadingOverlay>
          )}

          <TableInnerWrapper>
            <TableHeader tw='gap-[0 30px] xl:gap-[0 60px]' css={{ gridTemplateColumns }}>
              <TableHeaderCell>Tracking Pixel URL</TableHeaderCell>
              <TableHeaderCell>Campaign ID</TableHeaderCell>

              <TableHeaderCell>Campaign Order ID</TableHeaderCell>

              <TableHeaderCell>Created at</TableHeaderCell>
            </TableHeader>

            <TableBodyWrapper ref={tableRef}>{TableBodyElement}</TableBodyWrapper>
          </TableInnerWrapper>
        </TableWrapper>
      </Wrapper>
    </Modal>
  );
};
